import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';
import { useServerPagination } from '@/hooks/useServerPagination';
import { ListPagination } from '@/components/ListPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';
import { driverProfileName } from '@/lib/driverProfile';
import { coverExpiryBounds, coverExpiryStatus } from '@/lib/coverExpiry';

const ITEMS_PER_PAGE = 10;

type InsuranceStatus = 'all' | 'valid' | 'expiring_soon' | 'expired';
// Dropdown filter — the per-row statuses above plus a contract-aware option:
// "needs_renewal" = expired AND the latest policy of an active (IN_PROGRESS)
// contract (resolved server-side via RPC, since it spans contract status).
type StatusFilter = InsuranceStatus | 'needs_renewal';

// Good through its expiry date, on the Ghana day (G6): the one rule, in `coverExpiryStatus`.
export const getInsuranceStatus = (expiresAt: string | null): InsuranceStatus => {
  if (!expiresAt) return 'expired';
  const status = coverExpiryStatus(expiresAt);
  if (status === 'expired') return 'expired';
  if (status === 'expiring') return 'expiring_soon';
  return 'valid';
};

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

export const getExpiryDate = (expiresAt: string | null): string => {
  if (!expiresAt) return '-';
  const expiryDate = new Date(expiresAt);
  return expiryDate.toLocaleDateString();
};

export default function InsurancePolicies() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const trimmedSearch = debouncedSearch.trim();
  const isSearchActive = trimmedSearch.length > 0;
  const isNeedsRenewal = statusFilter === 'needs_renewal';

  // Search spans related tables (vehicle make/model/registration, driver name),
  // which a single PostgREST query can't OR across — so resolve the matching
  // insurance ids via RPC (mirrors the get_*_ids_by_* pattern used elsewhere)
  // and filter by them. Status, by contrast, derives purely from expires_at, so
  // it filters with date conditions directly. Both are applied to the count AND
  // the page query so pagination reflects the filtered set, not just one page.
  const { data: searchIds } = useQuery({
    queryKey: ['insurance-search-ids', trimmedSearch],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('get_insurance_ids_by_search', { p_search: trimmedSearch });
      if (error) throw error;
      return (data as number[]) || [];
    },
    enabled: isSearchActive,
  });

  // "Needs renewal" spans contract status (active) and "latest policy", so it's
  // resolved to matching insurance ids server-side via RPC, then filtered by id.
  const { data: needsRenewalIds } = useQuery({
    queryKey: ['insurance-needs-renewal-ids'],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc('get_insurance_ids_needing_renewal');
      if (error) throw error;
      return (data as number[]) || [];
    },
    enabled: isNeedsRenewal,
  });

  const searchReady = !isSearchActive || searchIds !== undefined;
  const renewalReady = !isNeedsRenewal || needsRenewalIds !== undefined;
  const filtersReady = searchReady && renewalReady;
  const noResults = isSearchActive && searchIds !== undefined && searchIds.length === 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applyFilters = (query: any) => {
    let q = query;
    // Mirror getInsuranceStatus exactly: the same G6 rule, as the Ghana-day bounds `coverExpiryBounds` gives.
    const { expiredBefore, validFrom } = coverExpiryBounds();
    // Quote the timestamp: dots in the ISO value are reserved in PostgREST's or() syntax.
    if (statusFilter === 'expired') q = q.or(`expires_at.is.null,expires_at.lt."${expiredBefore}"`);
    else if (statusFilter === 'expiring_soon') q = q.gte('expires_at', expiredBefore).lt('expires_at', validFrom);
    else if (statusFilter === 'valid') q = q.gte('expires_at', validFrom);
    else if (statusFilter === 'needs_renewal') q = q.in('id', needsRenewalIds ?? []);
    if (isSearchActive && searchIds && searchIds.length > 0) q = q.in('id', searchIds);
    return q;
  };

  // Count of the filtered set — drives pagination.
  const { data: countData } = useQuery({
    queryKey: ['insurances-count', statusFilter, trimmedSearch, searchIds, needsRenewalIds],
    placeholderData: keepPreviousData,
    enabled: filtersReady,
    queryFn: async () => {
      if (noResults) return 0;
      const { count, error } = await applyFilters(
        supabase.from('insurances').select('*', { count: 'exact', head: true })
      );
      if (error) throw error;
      return count || 0;
    },
  });

  const pagination = useServerPagination({
    totalCount: countData || 0,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const { sortKey, sortDirection, handleSort } = useTableSort({
    defaultSortKey: 'expires_at',
    defaultSortDirection: 'desc',
    onSortChange: pagination.resetPage,
  });

  // Current page of the filtered set, with related data for display.
  const { data: insurances, isLoading } = useQuery({
    queryKey: ['insurances', pagination.currentPage, sortKey, sortDirection, statusFilter, trimmedSearch, searchIds, needsRenewalIds],
    enabled: filtersReady && countData !== undefined,
    queryFn: async () => {
      if (noResults) return [];
      const query = supabase
        .from('insurances')
        .select(`
          *,
          vehicles(id, make, model, vehicle_registrations(number)),
          insurance_contracts(
            contracts(
              id,
              drivers(profiles(id, admin_id, first_name, last_name)),
              contract_updates(status, created_at)
            )
          ),
          insurance_premiums(transactions(amount))
        `)
        .order(sortKey || 'expires_at', { ascending: sortDirection === 'asc' });
      const { data, error } = await applyFilters(query).range(pagination.from, pagination.to);
      if (error) throw error;
      // Flatten: expose first contract at top level for display compatibility
      return data?.map(ins => ({
        ...ins,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        contracts: (ins as any).insurance_contracts?.[0]?.contracts || null,
      }));
    },
  });

  useEffect(() => { pagination.resetPage(); }, [statusFilter, trimmedSearch]);

  // Show the skeleton while resolving search ids, the count, or the page —
  // never the previous filter's rows.
  const showLoading = isLoading || !filtersReady || countData === undefined;

  const getStatusBadge = (status: InsuranceStatus) => {
    switch (status) {
      case 'valid': return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">Valid</Badge>;
      case 'expiring_soon': return <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700">Expiring Soon</Badge>;
      case 'expired': return <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">Expired</Badge>;
    }
  };

  // Process insurances for display
  const displayInsurances = (insurances ?? []).map(ins => {
    const vehicleReg = ins.vehicles?.vehicle_registrations;
    const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number;

    // The driver's own row and never an admin's test profile — see `driverProfileOf`.
    const driverName = driverProfileName(ins.contracts?.drivers?.profiles) ?? 'N/A';

    // Get premium from insurance_premiums -> transactions
    const premiumData = ins.insurance_premiums;
    const premium = Array.isArray(premiumData)
      ? premiumData.reduce((sum, p) => sum + (p.transactions?.amount || 0), 0)
      : (premiumData?.transactions?.amount || 0);

    return {
      ...ins,
      vehicleReg: regNumber,
      vehicleMake: ins.vehicles?.make,
      vehicleModel: ins.vehicles?.model,
      driverName,
      premium,
      status: getInsuranceStatus(ins.expires_at)
    };
  });

  return (
    <Card className="border-0">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search policies..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="needs_renewal">Renewal Due</SelectItem>
              <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
              <SelectItem value="valid">Valid</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {showLoading ? (
          <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : (
          <>
            <ListPagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalItems={pagination.totalItems} onPageChange={pagination.goToPage} />
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead>Vehicle</SortableTableHead>
                    <SortableTableHead>Driver</SortableTableHead>
                    <SortableTableHead align="right">Premium</SortableTableHead>
                    <SortableTableHead>Status</SortableTableHead>
                    <SortableTableHead sortKey="expires_at" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>Expiry</SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayInsurances.map((ins) => (
                    <TableRow key={ins.id}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{ins.vehicleReg || 'Unregistered'}</span>
                          <p className="text-xs text-muted-foreground">{ins.vehicleMake} {ins.vehicleModel}</p>
                        </div>
                      </TableCell>
                      <TableCell>{ins.driverName}</TableCell>
                      <TableCell className="text-right">{formatCurrency(ins.premium)}</TableCell>
                      <TableCell>{getStatusBadge(ins.status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{getExpiryDate(ins.expires_at)}</TableCell>
                    </TableRow>
                  ))}
                  {displayInsurances.length === 0 && !isLoading && (
                    <TableRow><TableCell colSpan={5} className="h-32 text-center text-muted-foreground">{search || statusFilter !== 'all' ? 'No policies match your filters' : 'No insurance policies found'}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
